import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, catchError, map, of, shareReplay } from 'rxjs';
import { Category, Brand } from '@core/models';
import { StoreConfigService } from './store-config.service';
import { swrGet } from '@core/utils/swr-get';
import { environment } from '@environments/environment';

function mapRawCategory(raw: any): Category {
  return {
    id: raw._id,
    name: raw.name,
    slug: raw.slug,
    description: raw.description,
    image: raw.image || raw.icon || '', // vacío: <app-media> pinta el ícono
    productCount: typeof raw.productCount === 'number' ? raw.productCount : undefined,
    parentId: raw.parentCategory?._id || raw.parentCategory || undefined,
  };
}

function mapRawBrand(raw: any): Brand {
  return {
    id: raw._id,
    name: raw.name,
    slug: raw.slug,
    logo: raw.logo || undefined,
    productCount: typeof raw.productCount === 'number' ? raw.productCount : undefined,
  };
}

@Injectable({ providedIn: 'root' })
export class CategoryService {
  private http = inject(HttpClient);
  private storeConfig = inject(StoreConfigService);

  /**
   * Caché por slug: antes guardaba la primera tienda visitada y, al pasar de una tienda
   * a otra (o de un slug inexistente a uno real), el header seguía mostrando la anterior.
   * Un error no se cachea: el siguiente pedido reintenta.
   */
  private cache = new Map<string, Observable<any[]>>();

  private cached<T>(kind: 'categories' | 'brands', mapper: (raw: any) => T): Observable<T[]> {
    const slug = this.storeConfig.currentSlug();
    const key = `${kind}:${slug}`;
    if (!this.cache.has(key)) {
      this.cache.set(
        key,
        swrGet<any>(this.http, `${environment.apiUrl}/store/${slug}/${kind}`).pipe(
          map((res) => (res.data || []).map(mapper)),
          catchError(() => {
            this.cache.delete(key);
            return of([]);
          }),
          shareReplay(1),
        ),
      );
    }
    return this.cache.get(key)! as Observable<T[]>;
  }

  getAll(): Observable<Category[]> {
    return this.cached('categories', mapRawCategory);
  }

  /** Para navegar (header, buscador, /categorias, filtros): sin las que no tienen nada publicado. */
  getVisible(): Observable<Category[]> {
    return this.getAll().pipe(map((cats) => cats.filter((c) => c.productCount !== 0)));
  }

  getBySlug(slug: string): Observable<Category | undefined> {
    return this.getAll().pipe(map((cats) => cats.find((c) => c.slug === slug)));
  }

  /** Resuelve el id real de una categoría a partir de su slug (usado para filtrar productos). */
  resolveIdBySlug(slug: string): Observable<string | undefined> {
    return this.getAll().pipe(map((cats) => cats.find((c) => c.slug === slug)?.id));
  }

  getBrands(): Observable<Brand[]> {
    return this.cached('brands', mapRawBrand);
  }

  /** Para filtrar: sin las marcas que no tienen nada publicado. */
  getVisibleBrands(): Observable<Brand[]> {
    return this.getBrands().pipe(map((brands) => brands.filter((b) => b.productCount !== 0)));
  }
}
